
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { useEffect } from 'react';
import { GetStaticPaths, GetStaticPathsResult, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import getT from 'next-translate/getT';
import { Translate } from 'next-translate';
import { unstable_serialize } from 'swr';
import { BreadcrumbJsonLd, NextSeo, ProductJsonLd } from 'next-seo';
import { captureException } from '@sentry/nextjs';
import i18nConfig from '../../i18n';
import { useApi } from '../../services/useApi';
import { decycle, parseResponse } from '../../services/useApiHelpers';
import PageWithFallback from '../../components/Catalog/WithFallback';
import { getOrderedProductCategories, getProductAddToCartLink, getProductMedia, getProductPath } from '../../lib/product';
import { CountryAndLocale, Locale, LocaleHreflangMap } from '../../external';
import fetcher, { strapiFetcher } from '../../utils/fetcher';
import { MediaType, Product } from '../../utils/types/Product';
import { IResponseDocument, isGetSuccess } from '../../utils/types/aimeosApi';
import { getCountryAndLocalesLists, getCountryAndLocaleStrings, getCurrencyFromCountryAndLocale } from '../../utils/locales';
import { getSEOProps } from '../../next-seo.config';
import useProductSeoData from '../../components/SingleProduct/hooks/useProductSeoData';
import { getSupplierDeliveryDetails } from '../../lib/service';
import SingleProductView from '../../components/SingleProduct/SingleProduct';
import useTrackingContext from '../../services/trackingContext/useTrackingContext';
import { ProductBaseIncludes, ProductListFieldsQuery } from '../../utils/constants';
import Error404 from '../404';
import { getLocalizedProductUrls } from '../../lib/urls';
import { StrapiCategoryResp } from '../../utils/types/strapi';
import { getStrapiCategoryUrl, getStrapiSupplierUrl } from '../../utils/strapi';
import cypressTestSlugs from '../../cypress/fixtures/product-slugs.json';
import { AggregateRatingProductSchemaProps, ReviewMinimized } from '../../utils/types/Review';
import { fetchReviewQuantities, fetchReviews, getAggregatedRatingsForProductSchema } from '../../utils/reviewUtils';
const { locales: countryAndLocaleList, defaultLocale: configDefaultLocale } = i18nConfig;
const { locales } = getCountryAndLocalesLists(countryAndLocaleList);
export interface ProductViewProps {
    productId: string;
    reviews?: ReviewMinimized[];
    aggregateRating?: AggregateRatingProductSchemaProps;
    supplierId?: string;
    localizedUrls?: LocaleHreflangMap;
}
interface IdQueryParam {
    id: string;
    [key: string]: string;
}
const ProductView: NextPage<ProductViewProps> = (props) => {
    const { productId, reviews, aggregateRating, supplierId, localizedUrls } = props;
    // Translations and locale
    const { locale: countryAndLocaleString, defaultLocale: defaultCountryAndLocale, asPath, } = useRouter();
    const { dispatchTrackingContext } = useTrackingContext();
    const { locale, country } = getCountryAndLocaleStrings(countryAndLocaleString);
    const { locale: defaultLocale } = getCountryAndLocaleStrings(defaultCountryAndLocale);
    // Get single product data
    const { getResource, getSingleResource } = useApi();
    const query = `&include=${ProductBaseIncludes.join(',')}${ProductListFieldsQuery}`;
    const { data: product, links } = getSingleResource<Product>('product', `${query}&id=${productId}`, false);
    // Get products from the same supplier for product carousel
    const { data, refresh } = getResource<Product>('product', `${query}&filter[f_catid]=1&filter[f_supid]=${supplierId}&page[limit]=12`);
    decycle('product', data);
    // Get supplier
    const supplier = product?.supplier?.[0];
    // Get add to cart link
    const addToCartLink = getProductAddToCartLink(links);
    useEffect(() => {
        // When user switches back to default locale, refresh product data so that,
        // correct product texts from API are used
        // Instead of localized ones assigned by block above
        if (locale === defaultLocale) {
            refresh();
        }
    }, [locale]);
    // Populate utils to tracking context for RFQ / Sample request flows
    useEffect(() => {
        dispatchTrackingContext({ util: { product, localizedUrls } });
    }, [product?.['product.code']]);
    const { isAvailable: isProductAvailableInCountry } = getSupplierDeliveryDetails(supplier, country);
    // SEO props
    const { seoTitle, seoDescription, productSchemaProps, breadcrumbSchemaProps, canonicalUrl, hreflangUrls, } = useProductSeoData({
        product: product as Product,
        reviews,
        aggregateRating,
        supplier,
        localizedUrls,
    });
    const media = product ? getProductMedia(product) : '';
    const imageAlt = (media as MediaType[])?.[0]?.originalAlt || '';
    const createSEOProps = getSEOProps();
    if (!product) {
        // The static generated page does not contain product data
        return <Error404 />;
    }
    return (<>
      {productSchemaProps && (<ProductJsonLd {...productSchemaProps}/>)}
      <BreadcrumbJsonLd {...breadcrumbSchemaProps}/>
      <NextSeo {...createSEOProps({
        title: seoTitle,
        path: asPath,
        description: seoDescription,
        imageUrl: media ? media[0]?.original : '',
        imageAlt,
        hreflangUrls,
        canonicalUrl,
    })} noindex={!isProductAvailableInCountry}/>
      <SingleProductView products={data} // Sending whole products array as we don't want to filter at this stage
     product={product} supplier={supplier} addToCartLink={addToCartLink} media={media || []}/>
    </>);
};
export default PageWithFallback(ProductView);
const getStaticProps: GetStaticProps<ProductViewProps, IdQueryParam> = async ({ params, locale: countryAndLocale, defaultLocale: defaultCountryAndLocale, }) => {
    const apiUrl = process.env.NEXT_PUBLIC_API_URL;
    const { locale, } = getCountryAndLocaleStrings((countryAndLocale || defaultCountryAndLocale));
    const currency = getCurrencyFromCountryAndLocale(countryAndLocale as CountryAndLocale);
    const tUrl = await getT(locale, 'url');
    const [codeSlice, nameSlice] = params?.id.split('+') || [];
    const productCode = codeSlice?.toUpperCase();
    const productName = nameSlice?.toLowerCase();
    const revalidate = Number(process.env.REGENERATE_INTERVAL) || 60;
    if (!productCode) {
        return {
            notFound: true,
            revalidate,
        };
    }
    const query = `include=${ProductBaseIncludes.join(',')}${ProductListFieldsQuery}`;
    // Fetch all products where the product.code CONTAINS the code from the url
    const singleProductUrl = `${apiUrl}/jsonapi/default/product?currency=${currency}&locale=${locale}&${query}&filter[%3D%3D][product.code]=${productCode}`;
    try {
        const singleProductResp = await fetcher(singleProductUrl);
        const respData = await singleProductResp.json();
        const { data } = parseResponse<Product>(respData);
        const product = data.find((prod) => prod['product.code'] === productCode);
        if (!product) {
            return {
                notFound: true,
                revalidate,
            };
        }
        const productId = product.id;
        const singleProductUrlWithId = `${apiUrl}/jsonapi/default/product?currency=${currency}&locale=${locale}&${query}&id=${productId}`; // Used for fallback data
        const reviews = await fetchReviews(productId, 10);
        const ratingQuantities = await fetchReviewQuantities(productId);
        const aggregateRating = getAggregatedRatingsForProductSchema(ratingQuantities);
        const t = await getT(locale, 'products');
        const supplierId = product?.supplier[0]['supplier.id'];
        const supplierCode = product?.supplier[0]['supplier.code'];
        const optionsUrl = `${apiUrl}/jsonapi/default?currency=${currency}&locale=${locale}`;
        const [optionsResp,] = await Promise.all([
            fetcher(optionsUrl, 'OPTIONS').then((resp) => resp.json()),
        ]);
        let apiProductName = '';
        try {
            apiProductName = getProductPath(product, t);
        }
        catch (e) {
            captureException(e);
            console.log(e);
            console.error('Product page build error', product);
        }
        if (!isGetSuccess(respData)) {
            console.error(`Could not fetch page ${params?.id} - received incorrect data - `, respData);
            return {
                notFound: true,
                revalidate,
            };
        }
        if (productName !== apiProductName) {
            console.error(`For locale ${countryAndLocale} - Product name does not match product name on server ---frontend--- ${productName} ---api--- ${apiProductName}, CODE: ${productCode}`);
            return {
                redirect: {
                    permanent: true,
                    destination: `/${countryAndLocale}/${tUrl('product')}/${productCode.toLowerCase()}+${apiProductName}`,
                },
            };
        }
        // Strapi
        // Used for SWR serialization
        let singleProductCategoryContentUrl = '';
        let singleProductCategoryContentResp = null;
        let singleProductSupplierContentUrl = '';
        let singleProductSupplierContentResp = null;
        try {
            // Order top 3 product categories by level
            const productCategories = getOrderedProductCategories(product)
                .slice(0, 3);
            const level2CategoryCode = productCategories[1]['catalog.code'];
            singleProductCategoryContentUrl = getStrapiCategoryUrl(level2CategoryCode, locale);
            // Fetch category content from Strapi
            singleProductCategoryContentResp = await strapiFetcher<StrapiCategoryResp>(singleProductCategoryContentUrl, countryAndLocale);
            // Fetch supplier content from Strapi
            singleProductSupplierContentUrl = getStrapiSupplierUrl(supplierCode, locale);
            singleProductSupplierContentResp = await strapiFetcher<StrapiCategoryResp>(singleProductSupplierContentUrl, countryAndLocale);
        }
        catch (e) {
            captureException(e);
            console.log(e);
            console.error('Product page build error', product);
        }
        // SEO hreflang urls
        const localizedUrls = await getLocalizedProductUrls(apiUrl || '', product);
        const fetchParams = { method: 'OPTIONS' };
        const isReviewed = !!(reviews.length && Number(aggregateRating?.reviewCount ?? '0'));
        const reviewSchemaData = isReviewed
            ? {
                reviews,
                aggregateRating,
            }
            : undefined;
        return {
            props: {
                fallback: {
                    [unstable_serialize(() => singleProductSupplierContentUrl)]: singleProductSupplierContentResp,
                    [unstable_serialize(() => singleProductCategoryContentUrl)]: singleProductCategoryContentResp,
                    [unstable_serialize([optionsUrl, JSON.stringify(fetchParams)])]: optionsResp,
                    [unstable_serialize(() => singleProductUrlWithId)]: respData,
                },
                productId,
                supplierId,
                localizedUrls,
                ...reviewSchemaData,
            },
            revalidate,
        };
    }
    catch (e) {
        captureException(e);
        console.error(`Could not generate page ${params?.id} - `, e);
        return {
            notFound: true,
            revalidate,
        };
    }
};
export const getStaticPaths: GetStaticPaths<IdQueryParam> = !process.env.RUNNING_CYPRESS
    ? async () => {
        const apiUrl = process.env.NEXT_PUBLIC_API_URL;
        // List of locales that will get pre-generated pages
        const localesToPrefetch: Partial<CountryAndLocale>[] = [
            configDefaultLocale as CountryAndLocale,
            'fi-fi',
            'sv-sv',
            'de-de',
        ];
        const localeMappedProdTranslations: Record<Locale, Translate> = Object.fromEntries(await Promise.all(locales.map(async (locale) => ([locale, await getT(locale, 'products')]))));
        const staticPathProps: GetStaticPathsResult<IdQueryParam> = {
            paths: [],
            fallback: 'blocking',
        };
        try {
            // Create an object where the key is a lang locale and the value is the products data for that
            // locale (containing all the localized product names that will later be generated on GSP)
            const localeMappedProducts: Record<Locale, Product[]> = Object.fromEntries(await Promise.all(localesToPrefetch.map(async (countryAndLocale) => {
                const { locale } = getCountryAndLocaleStrings(countryAndLocale);
                const limit = 50; // Number of products to fetch in each API call
                const totalProducts = 100; // Total num of products to fetch
                const numCalls = Math.ceil(totalProducts / limit); // Calc the num of API calls
                const dataPromises: Promise<Product[]>[] = Array.from({
                    length: numCalls,
                }).map(async (_, i) => {
                    const offset = i * limit;
                    const query = `?locale=${locale}&include=product/attribute,product/media,price,product,product/property,product/text,catalog,supplier,stock&page[limit]=${limit}&page[offset]=${offset}&fields[product.lists]=product.lists.id,product.lists.type&fields[attribute.lists]&fields[catalog.lists]&fields[supplier.lists]`;
                    const resp = await fetcher(`${apiUrl}/jsonapi/default/product${query}`);
                    const products: IResponseDocument = await resp.json();
                    const { data: responseData, error } = parseResponse<Product>(products);
                    if (error) {
                        console.error('Could not fetch product paths - received incorrect data - ', error);
                    }
                    return responseData;
                });
                const data = await Promise.all(dataPromises);
                return [locale, data.flat()];
            })));
            // For each locale access the products data with the locale and generate the correct path
            // with the product name matching the locale
            staticPathProps.paths = localesToPrefetch.flatMap((countryAndLocale) => {
                const { locale } = getCountryAndLocaleStrings(countryAndLocale);
                return localeMappedProducts?.[locale]?.map((product) => {
                    const productName = getProductPath(product, localeMappedProdTranslations[locale]);
                    return {
                        params: {
                            id: `${product['product.code'].toLowerCase()}+${productName}`,
                        },
                        locale: countryAndLocale,
                    };
                });
            });
        }
        catch (e) {
            captureException(e);
            console.error('Could not generate product paths - ', e);
        }
        return staticPathProps;
    } : async () => ({
    paths: cypressTestSlugs.map((slug) => ({ params: { id: slug } })),
    fallback: 'blocking',
});

    async function __Next_Translate__getStaticProps__1983cd98e8b__(ctx) {
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/product/[id]',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1983cd98e8b__ as getStaticProps }
  